import moment from 'moment'

export const calculateSuspensionData = (suspension) => {
  if (!suspension || !suspension.startDate) {
    return null
  }

  const startDate = moment(suspension.startDate)
  const endDate = suspension.endDate ? moment(suspension.endDate) : null
  const isActive = !endDate
  const isGptp = suspension.code === 'GPTP'

  const daysActive = isActive ? moment().diff(startDate, 'days') : null

  const terminationDate = isGptp && isActive ? startDate.clone().add(2, 'years') : null

  const daysUntilTermination = terminationDate ? terminationDate.diff(moment(), 'days') : null

  const monthsUntilTermination = terminationDate 
    ? Math.floor(daysUntilTermination / 30) 
    : null
  const remainingDays = terminationDate 
    ? daysUntilTermination % 30 
    : null

  return {
    code: suspension.code,
    startDate: suspension.startDate,
    endDate: suspension.endDate,
    note: suspension.note,
    isActive,
    isGptp,
    daysActive,
    terminationDate: terminationDate ? terminationDate.format('YYYY-MM-DD') : null,
    daysUntilTermination,
    monthsUntilTermination,
    remainingDays,
    formattedStartDate: startDate.format('MM/DD/YYYY'),
    formattedTerminationDate: terminationDate ? terminationDate.format('MM/DD/YYYY') : null,
    urgencyLevel: getUrgencyLevel(daysUntilTermination)
  }
}

const getUrgencyLevel = (daysUntilTermination) => {
  if (!daysUntilTermination || daysUntilTermination < 0) return null
  if (daysUntilTermination < 90) return 'critical'  // < 3 months
  if (daysUntilTermination < 180) return 'warning'  // < 6 months
  return 'info'
}

export const formatTimeRemaining = (suspensionData) => {
  if (!suspensionData || !suspensionData.isActive || !suspensionData.terminationDate) {
    return null
  }

  const { monthsUntilTermination, remainingDays } = suspensionData

  if (monthsUntilTermination === 0) {
    return `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`
  }

  const monthsText = `${monthsUntilTermination} ${monthsUntilTermination === 1 ? 'month' : 'months'}`
  const daysText = remainingDays > 0 
    ? `, ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}` 
    : ''

  return `${monthsText}${daysText}`
}

export const isCloseToTermination = (suspensionData) => {
  return suspensionData?.urgencyLevel === 'critical'
}

export const getSuspensionMessage = (suspensionData) => {
  if (!suspensionData) {
    return null
  }

  if (suspensionData.isGptp) {
    return {
      title: 'GPTP Suspension Active',
      description: 'You are suspended for not meeting acceptable progress requirements.',
      action: 'Complete your current level requirements to have the suspension lifted.',
      contactInfo: null
    }
  }

  switch (suspensionData.code) {
    case 'NPDU':
      return {
        title: 'Suspended - Non-Payment of Dues',
        description: 'Your membership is suspended due to non-payment of dues.',
        action: 'Please pay your outstanding dues to restore your membership.',
        contactInfo: 'Contact PGA Membership at (800) 474-2776'
      }
    default:
      return {
        title: 'Membership Suspended',
        description: 'Your membership is currently suspended.',
        action: 'Please review your suspension status.',
        contactInfo: 'Contact PGA Membership at (800) 474-2776 for details.'
      }
  }
}

export const shouldShowSuspensionWarning = (pgmProgress) => {
  if (!pgmProgress) return false
  return Boolean(pgmProgress.isSuspended && pgmProgress.suspensions?.length > 0)
}

export const getSuspensionFromProgress = (pgmProgress) => {
  if (!shouldShowSuspensionWarning(pgmProgress)) {
    return null
  }

  // Get the first suspension (earliest chronologically)
  const suspension = pgmProgress.suspensions[0]
  return calculateSuspensionData(suspension)
}

export const getEffectiveDeadline = (suspensionData, levelExpirationDate) => {
  if (!suspensionData) return null
  
  if (suspensionData.isGptp && suspensionData.terminationDate) {
    // For GPTP suspension: they need to complete the expired level within 2 years of suspension date or will be terminated
    return {
      date: suspensionData.terminationDate,
      formattedDate: suspensionData.formattedTerminationDate,
      type: 'termination',
      label: 'Complete requirements by',
      description: 'Termination from Associate Program if requirements not completed'
    }
  }

  if (levelExpirationDate) {
    return {
      date: levelExpirationDate,
      formattedDate: moment(levelExpirationDate).format('MM/DD/YYYY'),
      type: 'level_expiration',
      label: 'Level deadline',
      description: 'Original level completion deadline'
    }
  }
  
  return null
}
